import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Users } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { ClubForm, type ClubFormRef } from "@/components/club-form";
import { setActiveContextId } from "@/hooks/use-app-context";

export const Route = createFileRoute("/_authenticated/negocios/nuevo-club")({
  head: () => ({
    meta: [
      { title: "Crea tu club — Brulo" },
      { name: "description", content: "Crea tu club en Brulo y organiza salidas con tu comunidad." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: NuevoClubPage,
});

function NuevoClubPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formRef = useRef<ClubFormRef>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const result = await formRef.current?.submit();
      if (!result?.data) {
        const first = result?.errors ? Object.values(result.errors)[0] : null;
        toast.error(first ?? "Revisa los campos del formulario");
        return;
      }
      const d = result.data;

      const { data: club, error } = await supabase
        .from("clubs")
        .insert({
          owner_id: user.id,
          name: d.name.trim(),
          slug: "",
          type: d.type,
          city: d.city.trim(),
          estado: d.estado.trim() || null,
          member_count_range: d.member_count_range || null,
          description: d.description.trim() || null,
          instagram: d.instagram.trim() || null,
          phone: d.phone.trim() || null,
          contact_email: d.contact_email.trim() || null,
        })
        .select("id")
        .single();

      if (error || !club) throw error ?? new Error("No se pudo crear el club");

      const { error: linkError } = await supabase
        .from("club_managers")
        .insert({ club_id: club.id, user_id: user.id, role: "owner" });
      if (linkError) throw linkError;

      await queryClient.invalidateQueries({ queryKey: ["app-contexts", user.id] });
      setActiveContextId(club.id);
      toast.success(
        "Tu club fue registrado y está pendiente de aprobación. Te avisaremos cuando esté publicado.",
      );
      navigate({ to: "/dashboard" });
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "No se pudo crear tu club. Inténtalo de nuevo.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <span className="rounded-full bg-primary/10 p-2 text-primary">
            <Users className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold">Crea tu club</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Define la identidad de tu club. Tu registro quedará pendiente de
          aprobación antes de publicarse.
        </p>

        <div className="rounded-xl border border-border/60 bg-card/50 p-5 space-y-5">
          <ClubForm ref={formRef} />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="w-full min-h-11 rounded-md bg-primary px-4 py-3 font-semibold text-primary-foreground hover:bg-primary/90 transition disabled:opacity-60"
          >
            {saving ? "Creando…" : "Crear club"}
          </button>
        </div>
      </div>
    </div>
  );
}
